/**
 * Proportional bars, one per row (DESIGN.md "Workload bars"): an 8px bar in
 * the strong neutral scaled against the busiest row (or `max`), 2px minimum
 * for any non-zero value, with the row's name and a tabular count. It reads
 * the same at 2 drifts or 180 (the Proportional Chart Rule), so never draw
 * one mark per item instead. A list, so each row reads "Exchange, 63".
 */
import type { ReactNode } from 'react'
import clsx from 'clsx'

export interface WorkloadBarRow {
  label: string
  value: number
}

export interface WorkloadBarsProps {
  rows: WorkloadBarRow[]
  /** The value of a full-width bar. Default: the busiest row. */
  max?: number
  /** A helper line above the bars ("Open drifts by workload"). */
  caption?: ReactNode
  /** Accessible name of the list when there is no caption text. */
  label?: string
  className?: string
}

export function WorkloadBars({ rows, max, caption, label, className }: WorkloadBarsProps) {
  const top = Math.max(max ?? 0, ...rows.map((r) => r.value), 0)
  const digits = Math.max(2, ...rows.map((r) => String(r.value).length))
  return (
    <div className={clsx('min-w-0', className)}>
      {caption ? <p className="mb-2 text-helper text-text-helper">{caption}</p> : null}
      <ul aria-label={label ?? (typeof caption === 'string' ? caption : undefined)} className="m-0 grid list-none gap-1.5 p-0">
        {rows.map((row) => (
          <li
            key={row.label}
            className="grid items-center gap-3 text-helper text-text-secondary"
            style={{ gridTemplateColumns: `minmax(0,1fr) minmax(0,1fr) ${digits + 1}ch` }}
          >
            <span className="min-w-0 truncate">
              {row.label}
              <span className="sr-only">,</span>
            </span>
            <span aria-hidden="true" className="block h-2">
              {row.value > 0 ? (
                <span className="block h-full min-w-[2px] bg-text-primary" style={{ width: `${top ? (row.value / top) * 100 : 0}%` }} />
              ) : null}
            </span>
            <b className="text-right font-semibold tabular-nums text-text-primary">{row.value.toLocaleString('en-US')}</b>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default WorkloadBars
